import React, { useEffect, useRef, useState } from "react";
import { Calendar } from "lucide-react";

/**
 * Campo de fecha con formato único DÍA/MES/AÑO (dd/mm/aaaa) en todo el sistema.
 * Reemplaza a <input type="date">, cuyo formato depende del idioma del navegador
 * (en muchos equipos sale mm/dd/yyyy). Mantiene el mismo contrato:
 *   value    → "YYYY-MM-DD" o ""
 *   onChange → recibe un evento con e.target.value = "YYYY-MM-DD" o ""
 * Escribir 8 dígitos (ddmmaaaa) agrega las barras solo; el ícono abre el calendario.
 */
const isoToDisplay = (iso) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso || "");
  return m ? `${m[3]}/${m[2]}/${m[1]}` : "";
};

const maskDigits = (digits) => {
  const d = digits.slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

const displayToIso = (txt) => {
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(txt || "");
  if (!m) return null;
  const [, dd, mm, yyyy] = m;
  const d = Number(dd), mo = Number(mm), y = Number(yyyy);
  if (y < 1900 || y > 2100 || mo < 1 || mo > 12 || d < 1) return null;
  const dias = new Date(y, mo, 0).getDate();
  if (d > dias) return null;
  return `${yyyy}-${mm}-${dd}`;
};

const CLASES_WRAPPER = /^(w-|min-w-|max-w-|flex-|grow|shrink|col-span-|row-span-|basis-|self-|order-|md:w-|md:flex-|md:col-span-|sm:w-|sm:col-span-|lg:w-|lg:col-span-)/;

export default function DateInput({
  value = "", onChange, min, max, className = "", disabled, required, placeholder,
  style, id, name, onBlur, onFocus, autoFocus, title, readOnly, ...rest
}) {
  const [text, setText] = useState(isoToDisplay(value));
  const [editando, setEditando] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    if (!editando) setText(isoToDisplay(value));
  }, [value, editando]);

  const emitir = (iso) => {
    if (!onChange) return;
    const target = { value: iso, name, id, type: "date" };
    onChange({ target, currentTarget: target, persist() {}, preventDefault() {}, stopPropagation() {} });
  };

  const handleText = (e) => {
    const digits = e.target.value.replace(/\D/g, "");
    const masked = maskDigits(digits);
    setText(masked);
    if (digits.length === 0) { emitir(""); return; }
    const iso = displayToIso(masked);
    if (iso) emitir(iso);
  };

  const handleBlur = (e) => {
    setEditando(false);
    const iso = displayToIso(text);
    if (!iso) setText(isoToDisplay(value)); // incompleto o inválido: vuelve al valor guardado
    if (onBlur) onBlur(e);
  };

  const tokens = String(className || "").split(/\s+/).filter(Boolean);
  const wrapperCls = tokens.filter(t => CLASES_WRAPPER.test(t)).join(" ");
  const inputCls = tokens.filter(t => !CLASES_WRAPPER.test(t)).join(" ");

  const abrirCalendario = () => {
    const el = pickerRef.current;
    if (!el || disabled || readOnly) return;
    try { el.showPicker ? el.showPicker() : el.click(); } catch { el.click(); }
  };

  return (
    <div className={`relative ${wrapperCls || "inline-block"}`} style={wrapperCls ? undefined : { minWidth: "9.5rem" }}>
      <input
        {...rest}
        type="text"
        inputMode="numeric"
        id={id}
        name={name}
        title={title}
        autoFocus={autoFocus}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        placeholder={placeholder || "dd/mm/aaaa"}
        maxLength={10}
        autoComplete="off"
        value={text}
        onChange={handleText}
        onFocus={(e) => { setEditando(true); if (onFocus) onFocus(e); }}
        onBlur={handleBlur}
        className={`${inputCls} w-full`}
        style={{ ...(style || {}), paddingRight: "2rem" }}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={abrirCalendario}
        disabled={disabled || readOnly}
        className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white disabled:opacity-40"
        aria-label="Abrir calendario"
      >
        <Calendar className="w-4 h-4" />
      </button>
      {/* Selector nativo oculto: solo para elegir con calendario; el formato visible es siempre dd/mm/aaaa */}
      <input
        ref={pickerRef}
        type="date"
        tabIndex={-1}
        aria-hidden="true"
        value={value || ""}
        min={min}
        max={max}
        onChange={(e) => emitir(e.target.value)}
        style={{ position: "absolute", right: 0, bottom: 0, width: 1, height: 1, opacity: 0, pointerEvents: "none" }}
      />
    </div>
  );
}
